import { useIntl } from 'react-intl';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { API } from 'src/reactQuery/Api';
import { postRequest } from 'src/utils/request';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import messages from 'src/pages/DynamicFieldsPage/messages';
import { useMessageHandler } from 'src/utils/hooks/useMessageHandler';

import type { RequestSimplifiedData } from 'src/types/requestData';

export interface DynamicFieldParamsType {
  nameEn: string;
  nameDe: string;
  typeCode: string;
  position: number;
  questionEn: string;
  questionDe: string;
  helpTextEn: string;
  helpTextDe: string;
  description: string;
  categoryUuid: string;
  isVisibleByAdmin: boolean;
  isVisibleByUser: boolean;
}

const createDynamicField = async (dynamicField: DynamicFieldParamsType) => {
  const { data } = await postRequest<RequestSimplifiedData>(
    API.POST.dynamicField,
    buildSaveDynamicFieldParams(dynamicField),
    { headers: { Accept: 'application/vnd.falcon.v1+json' } },
  );

  return data;
};

export const useCreateDynamicField = () => {
  const client = useQueryClient();
  const { formatMessage } = useIntl();
  const { handleError, handleSuccess } = useMessageHandler();

  return useMutation({
    mutationFn: createDynamicField,
    onSuccess: () => {
      handleSuccess(formatMessage(messages.dynamicFieldSaved), 1000);
      client.invalidateQueries({ queryKey: [QUERY_KEYS.DYNAMIC_FIELDS] });
    },
    onError: handleError,
  });
};

export function buildSaveDynamicFieldParams(
  dynamicField: DynamicFieldParamsType,
) {
  return {
    dynamic_field: {
      name_en: dynamicField.nameEn,
      name_de: dynamicField.nameDe,
      question_en: dynamicField.questionEn,
      question_de: dynamicField.questionDe,
      help_text_en: dynamicField.helpTextEn,
      help_text_de: dynamicField.helpTextDe,
      description: dynamicField.description,
      field_type_code: dynamicField.typeCode,
      field_category_uuid: dynamicField.categoryUuid,
      is_visible_by_user: dynamicField.isVisibleByUser,
      is_visible_by_admin: dynamicField.isVisibleByAdmin,
      position: parseInt(dynamicField.position.toString(), 10),
    },
  };
}
